import {dialogApi} from '../../../../api/dialogApi'
import {ReactComponent as Like} from '../../../../assets/Icons/Like2.svg'
import {ReactComponent as Dislike} from '../../../../assets/Icons/Dislike.svg'
import {ReactComponent as Dialog} from '../../../../assets/Icons/Dialog.svg'
import {ReactComponent as People} from '../../../../assets/Icons/People.svg'
import {ReactComponent as MoreIcon} from '../../../../assets/Icons/MoreIcon.svg'
import {Popover} from '../../../../components/Popover/Popover'
import {useNavigate} from 'react-router'
import {useCallback, useRef, useState} from 'react'
import {conversationsPath} from '../../../../constants/pathConstants'
import {useWuShowToast, WuIcon} from '@npm-questionpro/wick-ui-lib'
import {BaseModal} from '../../../../components/BaseModal/BaseModal'
import {ConfirmModalContent} from '../../../../components/ConfirmModalContent'
import styles from './DialogList.module.scss'
import {useWt} from '@npm-questionpro/wick-ui-lib'
import {GLOBAL} from '../../../../Translation/English/global'
import {DIALOG} from '../../../../Translation/English/dialog'
import type {IDialog} from '../../../../types'

interface IDialogListProps {
  dialogList: IDialog[]
  isLoading: boolean
  isArchiveTab: boolean
  onRefresh: () => void
}

export const DialogList: React.FC<IDialogListProps> = ({
  dialogList,
  isLoading,
  isArchiveTab,
  onRefresh,
}) => {
  const wt = useWt()
  const {showToast} = useWuShowToast()
  const navigate = useNavigate()
  const updateDialogMutation = dialogApi.useUpdateDialog()
  const deleteDialogMutation = dialogApi.useDeleteDialog()
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedDialog, setSelectedDialog] = useState<IDialog | null>(null)
  const [content, setContent] = useState<React.ReactNode>(null)
  const isArchivingRef = useRef(false)

  const handleSelect = useCallback(
    async (type: string, objectParam?: unknown) => {
      const object = objectParam as IDialog | undefined
      if (!object) return
      if (type === 'edit') {
        navigate(`${conversationsPath}/${object.id}`, {
          state: {switchToSetupTab: true, isArchiveTab: isArchiveTab},
        })
      }
      if (type === 'archive') {
        if (isArchivingRef.current) return
        isArchivingRef.current = true
        try {
          await updateDialogMutation.mutateAsync({
            id: object.id,
            isAchived: !isArchiveTab,
          })
          onRefresh()
        } catch {
          showToast({
            message: wt(DIALOG.failedToUpdateConversationStatus),
            variant: 'error',
          })
        } finally {
          isArchivingRef.current = false
        }
      }
      if (type === 'delete') {
        setSelectedDialog(object)
        setShowDeleteModal(true)
      }
    },
    [isArchiveTab, navigate, updateDialogMutation, onRefresh, showToast, wt],
  )

  const redirectToDialog = (dialog: IDialog): void => {
    navigate(`${conversationsPath}/${dialog.id}`, {
      state: {isArchiveTab: isArchiveTab},
    })
  }

  const deleteDialog = async (): Promise<void> => {
    if (!selectedDialog?.id) return
    try {
      await deleteDialogMutation.mutateAsync({id: String(selectedDialog.id)})
      setShowDeleteModal(false)
      onRefresh()
    } catch {
      showToast({
        message: wt(DIALOG.failedToDeleteConversation),
        variant: 'error',
      })
    }
  }

  return (
    <>
      {dialogList?.map((dialog, index) => (
        <div
          className={styles.dialogItem}
          key={`dialog-item--${index}`}
          onClick={() => redirectToDialog(dialog)}
        >
          <Popover
            onSelect={handleSelect}
            classNames={{root: styles.popover}}
            content={[
              {
                icon: <WuIcon icon="wm-edit" className={styles.icon} />,
                name: `${wt(GLOBAL.edit)}`,
                value: 'edit',
                object: dialog,
              },
              {
                icon: isArchiveTab ? (
                  <WuIcon icon="wm-lock-open-right" className={styles.icon} />
                ) : (
                  <WuIcon icon="wm-lock-open" className={styles.icon} />
                ),
                name: isArchiveTab
                  ? `${wt(GLOBAL.open)}`
                  : `${wt(GLOBAL.close)}`,
                value: 'archive',
                object: dialog,
              },
              ...(isArchiveTab
                ? [
                    {
                      icon: <WuIcon icon="wm-delete" className={styles.icon} />,
                      name: `${wt(GLOBAL.delete)}`,
                      value: 'delete',
                      object: dialog,
                    },
                  ]
                : []),
            ]}
            icon={<MoreIcon />}
            ariaLabel={wt(GLOBAL.moreOptions)}
          />
          <div className={styles.title}>{dialog.title}</div>
          <div className={styles.question}>{dialog.questionText}</div>
          <div className={styles.stats}>
            <div className={styles.statItem}>
              <People />
              {dialog.totalParticipantsCount}{' '}
              {dialog.totalParticipantsCount === 1
                ? wt(DIALOG.participant)
                : wt(DIALOG.participants)}
            </div>
            <div className={styles.statItem}>
              <Dialog />
              {dialog.totalResponsesCount}{' '}
              {dialog.totalResponsesCount === 1
                ? wt(DIALOG.response)
                : wt(DIALOG.responses)}
            </div>
            {dialog.enableLikes && (
              <div className={styles.statItem}>
                <Like />
                {dialog.totalLikesCount}{' '}
                {dialog.totalLikesCount === 1
                  ? wt(DIALOG.like)
                  : wt(DIALOG.likes)}
              </div>
            )}
            {dialog.enableDislikes && (
              <div className={styles.statItem}>
                <Dislike />
                {dialog.totalDislikesCount}{' '}
                {dialog.totalDislikesCount === 1
                  ? wt(DIALOG.dislike)
                  : wt(DIALOG.dislikes)}
              </div>
            )}
          </div>
        </div>
      ))}
      {dialogList.length === 0 && !isLoading && (
        <div className={styles.empty}>{wt(DIALOG.noDialogs)}</div>
      )}
      <BaseModal
        visible={showDeleteModal}
        headerTitle={String(wt(DIALOG.deleteDialog))}
        onCloseModal={() => setShowDeleteModal(false)}
        maxWidth="600px"
        variant="critical"
        footer={content}
      >
        <ConfirmModalContent
          title={selectedDialog?.title}
          isLoading={deleteDialogMutation.isPending}
          description={`${wt(GLOBAL.deleteItemText)} ${wt(
            GLOBAL.conversation,
          )}?`}
          handleConfirm={deleteDialog}
          handleCancel={() => setShowDeleteModal(false)}
          setContent={setContent}
          isDestructive
        />
      </BaseModal>
    </>
  )
}
